import {useMemo, useState} from "react";
import {submitLead, en, ro as roDict, type FieldRule, type I18nKey} from "@zaaaz/shared";
import {useFormValidation} from "@/lib/useFormValidation";
import {FormField} from "./FormField";

const empty = {
  org_legal_name: "", org_trade_name: "", company_type: "", vat_id: "", industry: "",
  country: "", city: "", employees_total: "", employees_estimated: "",
  contact_name: "", contact_role: "", contact_email: "", contact_phone: "",
  hris: "", heard_from: "", notes: "", consent_terms: false
};

type ApplyField =
  | "org_legal_name" | "company_type" | "vat_id" | "industry" | "country" | "city"
  | "employees_total" | "employees_estimated" | "contact_name" | "contact_role"
  | "contact_email" | "contact_phone";

const applyRules: FieldRule<ApplyField>[] = [
  {field: "org_legal_name", required: true},
  {field: "company_type", required: true},
  {field: "vat_id", required: true},
  {field: "industry", type: "select", required: true},
  {field: "country", required: true},
  {field: "city", required: true},
  {field: "employees_total", type: "number", min: 1, required: true},
  {field: "employees_estimated", type: "number", min: 1, required: true},
  {field: "contact_name", required: true},
  {field: "contact_role", required: true},
  {field: "contact_email", type: "email", required: true},
  {field: "contact_phone", required: true}
];

const applyFieldKeys = new Set<string>(applyRules.map((r) => r.field));

export function PresentationApplyForm({lang, onClose}: {lang: "en" | "ro"; onClose: () => void}) {
  const [form, setForm] = useState(empty);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const {errors, clearError, validate} = useFormValidation<ApplyField>();
  const isRo = lang === "ro";
  const t = useMemo(() => {
    const dict = isRo ? roDict : en;
    return (key: I18nKey) => dict[key] ?? key;
  }, [isRo]);
  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr("");
    setMsg("");
    if(!form.consent_terms) {
      setErr(t("Please accept the terms and conditions"));
      return;
    }
    if(!validate(applyRules, form, t)) return;
    try {
      const payload: Record<string, string> = {
        org_legal_name: form.org_legal_name,
        org_trade_name: form.org_trade_name,
        company_type: form.company_type,
        vat_id: form.vat_id,
        industry: form.industry,
        country: form.country,
        city: form.city,
        employees_total: form.employees_total,
        employees_estimated: form.employees_estimated,
        contact_name: form.contact_name,
        contact_role: form.contact_role,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        hris: form.hris,
        heard_from: form.heard_from,
        notes: form.notes
      };
      if(form.consent_terms) payload.consent_terms = "1";
      await submitLead(payload);
      setMsg(isRo ? "Aplicația a fost trimisă. Te contactăm în curând." : "Application sent. We will contact you soon.");
      setForm(empty);
    } catch(e) {
      setErr(e instanceof Error ? e.message : (isRo ? "Trimitere eșuată" : "Send failed"));
    }
  }
  function setField<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((f) => ({...f, [key]: value}));
    if(applyFieldKeys.has(String(key))) clearError(key as ApplyField);
  }
  return (
    <div className="applyModal" style={{position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, overflow: "auto", padding: 20}} onClick={onClose}>
      <div className="applyModalInner" style={{maxWidth: 720, margin: "40px auto", background: "#fff", borderRadius: 16, padding: "30px 24px"}} onClick={(e) => e.stopPropagation()}>
        <h2>{isRo ? "Aplică pentru acces" : "Apply for access"}</h2>
        <p className="applyIntro">{isRo ? "Completează detaliile organizației tale." : "Fill in your organization details."}</p>
        <form className="applyForm" onSubmit={onSubmit} noValidate>
          <div className="applyGroup">
            <h3>{isRo ? "Detalii organizație" : "Organization details"}</h3>
            <div className="cols2 clearfix">
              <div className="line">
                <FormField error={errors.org_legal_name}>
                  <input type="text" value={form.org_legal_name} onChange={(e) => setField("org_legal_name", e.target.value)} placeholder={isRo ? "Denumire legală *" : "Legal name *"} className="text anim req" aria-required="true" />
                </FormField>
              </div>
              <div className="line"><input type="text" value={form.org_trade_name} onChange={(e) => setField("org_trade_name", e.target.value)} placeholder={isRo ? "Denumire comercială" : "Trade name"} className="text anim" /></div>
            </div>
            <div className="cols2 clearfix">
              <div className="line">
                <FormField error={errors.company_type}>
                  <input type="text" value={form.company_type} onChange={(e) => setField("company_type", e.target.value)} placeholder={isRo ? "Formă juridică *" : "Legal form *"} className="text anim req" aria-required="true" />
                </FormField>
              </div>
              <div className="line">
                <FormField error={errors.vat_id}>
                  <input type="text" value={form.vat_id} onChange={(e) => setField("vat_id", e.target.value)} placeholder={isRo ? "CUI *" : "VAT ID *"} className="text anim req" aria-required="true" />
                </FormField>
              </div>
            </div>
            <div className="cols2 clearfix">
              <div className="line">
                <FormField error={errors.industry}>
                  <select value={form.industry} onChange={(e) => setField("industry", e.target.value)} className="text anim req" aria-required="true">
                    <option value="">{isRo ? "Industrie *" : "Industry *"}</option>
                    <option value="IT & Software">IT & Software</option>
                    <option value="Professional Services">Professional Services</option>
                    <option value="Retail / eCommerce">Retail / eCommerce</option>
                    <option value="Manufacturing">Manufacturing</option>
                    <option value="Other">Other</option>
                  </select>
                </FormField>
              </div>
              <div className="line">
                <FormField error={errors.country}>
                  <input type="text" value={form.country} onChange={(e) => setField("country", e.target.value)} placeholder={isRo ? "Țară *" : "Country *"} className="text anim req" aria-required="true" />
                </FormField>
              </div>
            </div>
            <div className="cols2 clearfix">
              <div className="line">
                <FormField error={errors.city}>
                  <input type="text" value={form.city} onChange={(e) => setField("city", e.target.value)} placeholder={isRo ? "Oraș *" : "City *"} className="text anim req" aria-required="true" />
                </FormField>
              </div>
              <div className="line">
                <FormField error={errors.employees_total}>
                  <input type="number" min={1} value={form.employees_total} onChange={(e) => setField("employees_total", e.target.value)} placeholder={isRo ? "Nr. angajați *" : "Employees *"} className="text anim req" aria-required="true" />
                </FormField>
              </div>
            </div>
            <div className="line">
              <FormField error={errors.employees_estimated}>
                <input type="number" min={1} value={form.employees_estimated} onChange={(e) => setField("employees_estimated", e.target.value)} placeholder={isRo ? "Angajați estimați pe platformă *" : "Estimated platform users *"} className="text anim req" aria-required="true" />
              </FormField>
            </div>
          </div>
          <div className="applyGroup">
            <h3>{isRo ? "Persoană de contact" : "Contact person"}</h3>
            <div className="cols2 clearfix">
              <div className="line">
                <FormField error={errors.contact_name}>
                  <input type="text" value={form.contact_name} onChange={(e) => setField("contact_name", e.target.value)} placeholder={isRo ? "Nume *" : "Name *"} className="text anim req" aria-required="true" />
                </FormField>
              </div>
              <div className="line">
                <FormField error={errors.contact_role}>
                  <input type="text" value={form.contact_role} onChange={(e) => setField("contact_role", e.target.value)} placeholder={isRo ? "Funcție *" : "Role *"} className="text anim req" aria-required="true" />
                </FormField>
              </div>
            </div>
            <div className="cols2 clearfix">
              <div className="line">
                <FormField error={errors.contact_email}>
                  <input type="email" value={form.contact_email} onChange={(e) => setField("contact_email", e.target.value)} placeholder="E-mail *" className="text anim req" aria-required="true" />
                </FormField>
              </div>
              <div className="line">
                <FormField error={errors.contact_phone}>
                  <input type="text" value={form.contact_phone} onChange={(e) => setField("contact_phone", e.target.value)} placeholder={isRo ? "Telefon *" : "Phone *"} className="text anim req" aria-required="true" />
                </FormField>
              </div>
            </div>
          </div>
          <div className="line full">
            <label><input type="checkbox" checked={form.consent_terms} onChange={(e) => setForm({...form, consent_terms: e.target.checked})} /> {isRo ? "Accept termenii și condițiile *" : "I agree to the Terms & Conditions *"}</label>
          </div>
          {msg ? <div className="raspuns round">{msg}</div> : null}
          {err ? <div className="raspuns round not">{err}</div> : null}
          <div className="applyActions" style={{marginTop: 16}}>
            <button type="submit" className="buton anim primaryCta">{isRo ? "Trimite aplicația" : "Send application"}</button>
            <button type="button" className="buton anim" style={{marginLeft: 8}} onClick={onClose}>{isRo ? "Închide" : "Close"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
